/** @typedef {'errors'|'performance'|'http'|string} Telemetry */
/** @typedef {{ applicationId: string, config: Record<string, unknown>, namespace: string, region: string, source: string, version: string }} BootstrapSettings */
/** @typedef {{ c: Record<string, unknown>, i: string, n: string, q: Array<{ c: string, p: unknown }>, r: string, v: string }} AwsRumClientState */
/** @typedef {Window & typeof globalThis & { AwsRumClient?: AwsRumClientState } & Record<string, unknown>} RumWindow */

/**
 * @param {string|boolean|null|undefined} value
 * @param {boolean} [fallback]
 * @returns {boolean}
 */
function asBoolean(value, fallback = false) {
	if (value === undefined || value === null || value === '') {
		return fallback;
	}

	return value === 'true';
}

/**
 * @param {string|number|null|undefined} value
 * @param {number} fallback
 * @returns {number}
 */
function asNumber(value, fallback) {
	const parsed = Number(value);

	return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * @param {string|undefined} value
 * @returns {Telemetry[]}
 */
function parseTelemetries(value) {
	if (!value) {
		return ['errors', 'performance', 'http'];
	}

	try {
		const parsed = JSON.parse(value);

		if (Array.isArray(parsed)) {
			return parsed;
		}
	} catch {
		// Fall back to comma-separated parsing.
	}

	return value
		.split(',')
		.map((telemetry) => telemetry.trim())
		.filter(Boolean);
}

function getNamespace() {
	return process.env.REACT_APP_CLOUDWATCH_RUM_NAMESPACE || 'cwr';
}

function getRegion() {
	return process.env.REACT_APP_CLOUDWATCH_RUM_APP_MONITOR_REGION;
}

function canUseBrowserRuntime() {
	return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function hasRequiredConfig() {
	return Boolean(
		process.env.REACT_APP_CLOUDWATCH_RUM_APP_MONITOR_ID && getRegion()
	);
}

function canAuthenticate() {
	if (process.env.REACT_APP_CLOUDWATCH_RUM_IDENTITY_POOL_ID) {
		return true;
	}

	return (
		asBoolean(process.env.REACT_APP_CLOUDWATCH_RUM_SIGNING, true) === false
	);
}

function canUse() {
	if (!canUseBrowserRuntime()) {
		return false;
	}

	if ((process.env.REACT_APP_ENV || 'local') === 'local') {
		return false;
	}

	if (!hasRequiredConfig()) {
		return false;
	}

	return canAuthenticate();
}

function getClientUrl() {
	if (process.env.REACT_APP_CLOUDWATCH_RUM_CLIENT_URL) {
		return process.env.REACT_APP_CLOUDWATCH_RUM_CLIENT_URL;
	}

	return `https://client.rum.${getRegion()}.amazonaws.com/1.0.2/cwr.js`;
}

function getRumConfig() {
	return {
		allowCookies: asBoolean(
			process.env.REACT_APP_CLOUDWATCH_RUM_ALLOW_COOKIES,
			true
		),
		enableXRay: asBoolean(
			process.env.REACT_APP_CLOUDWATCH_RUM_ENABLE_XRAY,
			false
		),
		endpoint: process.env.REACT_APP_CLOUDWATCH_RUM_ENDPOINT || undefined,
		guestRoleArn:
			process.env.REACT_APP_CLOUDWATCH_RUM_GUEST_ROLE_ARN || undefined,
		identityPoolId:
			process.env.REACT_APP_CLOUDWATCH_RUM_IDENTITY_POOL_ID || undefined,
		sessionSampleRate: asNumber(
			process.env.REACT_APP_CLOUDWATCH_RUM_SESSION_SAMPLE_RATE,
			1
		),
		signing: asBoolean(process.env.REACT_APP_CLOUDWATCH_RUM_SIGNING, true),
		telemetries: parseTelemetries(
			process.env.REACT_APP_CLOUDWATCH_RUM_TELEMETRIES
		),
	};
}

/**
 * @param {BootstrapSettings} settings
 * @returns {AwsRumClientState}
 */
function createClientState(settings) {
	const client = {
		c: settings.config,
		n: settings.namespace,
		q: [],
		r: settings.region,
		v: settings.version,
	};

	Object.defineProperty(client, 'i', {
		configurable: true,
		enumerable: true,
		value: settings.applicationId,
		writable: true,
	});

	return /** @type {AwsRumClientState} */ (/** @type {unknown} */ (client));
}

/**
 * @param {BootstrapSettings} settings
 * @returns {void}
 */
function bootstrap(settings) {
	const rumWindow = /** @type {RumWindow} */ (window);
	const client = rumWindow.AwsRumClient || createClientState(settings);

	rumWindow.AwsRumClient = client;

	if (typeof rumWindow[settings.namespace] !== 'function') {
		rumWindow[settings.namespace] = function (
			/** @type {string} */ command,
			/** @type {unknown} */ payload
		) {
			client.q.push({
				c: command,
				p: payload,
			});
		};
	}

	const existingScript = document.querySelector(
		`script[data-rum-namespace="${settings.namespace}"]`
	);

	if (existingScript) {
		return;
	}

	const script = document.createElement('script');
	script.async = true;
	script.dataset.rumNamespace = settings.namespace;
	script.src = settings.source;
	document.head.insertBefore(script, document.head.firstChild);
}

let initialized = false;

const CloudWatchMonitor = {
	/**
	 * @param {unknown} error
	 * @returns {void}
	 */
	captureException(error) {
		CloudWatchMonitor.initialize();
		const rumWindow = /** @type {RumWindow} */ (window);
		const rumCommand = rumWindow[getNamespace()];

		if (typeof rumCommand === 'function') {
			rumCommand('recordError', error);
		}
	},

	initialize() {
		if (initialized || !canUse()) {
			return;
		}

		try {
			const applicationId =
				process.env.REACT_APP_CLOUDWATCH_RUM_APP_MONITOR_ID;
			const region = getRegion();

			if (!applicationId || !region) {
				return;
			}

			bootstrap({
				applicationId,
				config: getRumConfig(),
				namespace: getNamespace(),
				region,
				source: getClientUrl(),
				version: process.env.REACT_APP_VERSION || '0.1.0',
			});
			initialized = true;
		} catch (error) {
			/* eslint-disable-next-line no-console -- Monitoring bootstrap failures should not crash the app */
			console.error('Failed to initialize CloudWatch RUM.', error);
		}
	},
};

export default CloudWatchMonitor;
